/** TikTok 平台工作区路由常量（店铺类型路径段 + 公用页面） */

import { openUrlInNewTab } from '@/config/ipeRoutes';
import i18n from '@/i18n';
import { bindNavLabels, copyNavItem } from '@/i18n/bindCopy';
import { platformNavLabel } from '@/i18n/platformNavLabel';
import { normalizeTiktokShopType } from '@/utils/platformShopCatalog';

export const IPE_TIKTOK_BASE_PATH = '/ipe/tiktok';
export const IPE_TIKTOK_GATE_PATH = `${IPE_TIKTOK_BASE_PATH}/gate`;
/** Partner Center / 无 state 回调：落地选未授权店，确认后换票并同步店铺信息 */
export const IPE_TIKTOK_AUTH_CODE_PATH = `${IPE_TIKTOK_BASE_PATH}/auth-code`;
export const TIKTOK_NOTICE_HELP_PATH = `${IPE_TIKTOK_BASE_PATH}/help`;

/** URL 类型段 slug */
export const TIKTOK_SHOP_TYPE_SLUGS = ['crossborder', 'local', 'full'];

export const TIKTOK_SHOP_TYPES = ['跨境', '本土', '全托管'];

export const TIKTOK_ACTIVE_SHOP_STORAGE_KEY = 'ipe_tiktok_active_shop_id';
export const TIKTOK_WORKSPACE_TOKEN_KEY = 'ipe_tiktok_workspace_token';
export const TIKTOK_WORKSPACE_SHOP_TYPE_KEY = 'ipe_tiktok_workspace_shop_type';
export const TIKTOK_SHOP_SWITCH_TOAST_KEY = 'ipe_tiktok_shop_switch_toast';

/** @deprecated 兼容旧三轨命名 */
export const TIKTOK_TRACKS = TIKTOK_SHOP_TYPE_SLUGS;

export const TIKTOK_PRODUCT_SUBNAV = [
  { id: 'publish', label: '发布产品', path: 'publish', openInNewTab: true },
  { id: 'info', label: '产品信息', path: '' },
  { id: 'records', label: '发布记录', path: 'records' },
  { id: 'drafts', label: '草稿箱', path: 'drafts' },
];

/** 侧栏可见模板子项（实拍/证书/建议价/模特库隐藏，路由保留） */
export const TIKTOK_TEMPLATE_SUBNAV = [
  { id: 'product', label: '产品模板', path: 'product' },
  { id: 'size-chart', label: '尺码库', path: 'size-chart' },
  { id: 'attributes', label: '属性库', path: 'attributes' },
  { id: 'brands', label: '品牌库', path: 'brands' },
  { id: 'warehouse', label: '发货仓库', path: 'warehouse' },
  { id: 'freight', label: '运费模板', path: 'freight' },
];

/** 本土店不展示发货仓库/运费模板（发品不发站点·仓·运费，平台按本土物流计费） */
export const TIKTOK_TEMPLATE_SUBNAV_SEMI_ONLY_IDS = new Set(['warehouse', 'freight']);

/** 按店铺类型返回模板库侧栏子项 */
export function templateSubnavForShopTypeSlug(shopTypeSlug) {
  const slug = normalizeTiktokShopTypeSlug(shopTypeSlug) || 'crossborder';
  if (slug === 'full' || slug === 'local') {
    return TIKTOK_TEMPLATE_SUBNAV.filter((c) => !TIKTOK_TEMPLATE_SUBNAV_SEMI_ONLY_IDS.has(c.id));
  }
  return TIKTOK_TEMPLATE_SUBNAV;
}

/** 全量子项（含隐藏），供直链标题解析 */
export const TIKTOK_TEMPLATE_SUBNAV_ALL = [
  ...TIKTOK_TEMPLATE_SUBNAV,
  { id: 'model', label: '模特库', path: 'model' },
  { id: 'photo', label: '实拍图库', path: 'photo' },
  { id: 'certificates', label: '证书库', path: 'certificates' },
  { id: 'rrp', label: '建议价资料库', path: 'rrp' },
];

/** 产品合规侧栏（仅保留合规资料） */
export const TIKTOK_COMPLIANCE_SUBNAV = [
  { id: 'materials', label: '合规资料', path: 'materials' },
];

/** @deprecated 包装/广告/寄样已并入订单履约子导航 */
export const TIKTOK_COMPLIANCE_SUBNAV_FULL_EXTRA = [
  { id: 'packing', label: '包装合规', path: 'packing' },
  { id: 'ads', label: '商品广告', path: 'ads' },
  { id: 'sample-qc', label: '寄样质检', path: 'sample-qc' },
];

/** @deprecated TTS 无独立价格菜单；改价在产品信息编辑页完成 */
export const TIKTOK_PRICE_SUBNAV = [];

export const TIKTOK_ORDER_SUBNAV = [
  { id: 'list', label: '订单列表', path: '' },
  { id: 'returns', label: '退货退款', path: 'returns' },
  { id: 'cancellations', label: '取消单', path: 'cancellations' },
];

/** 全托管遗留子导航：仅保留经营数据，履约写/广告不进侧栏 */
export const TIKTOK_FULFILLMENT_ORDER_SUBNAV = [
  { id: 'sales', label: '经营数据', path: 'sales' },
];

export const TIKTOK_MARKETING_SUBNAV = [
  { id: 'activities', label: '活动列表', path: '' },
  { id: 'enrolls', label: '活动商品', path: 'enrolls' },
  { id: 'coupons', label: '优惠券', path: 'coupons' },
];

/** 数据分析一级菜单子项 */
export const TIKTOK_ANALYTICS_SUBNAV = [
  { id: 'overview', label: '经营数据', path: '' },
];

/** 半托 / 本土侧栏（不含全托履约专属项） */
export const TIKTOK_NAV_BASE = [
  { id: 'home', label: '首页', path: 'home' },
  {
    id: 'products',
    label: '产品管理',
    path: 'products',
    children: TIKTOK_PRODUCT_SUBNAV,
  },
  {
    id: 'compliance',
    label: '产品合规',
    path: 'compliance',
    children: TIKTOK_COMPLIANCE_SUBNAV,
  },
  {
    id: 'templates',
    label: '模板库',
    path: 'templates',
    children: TIKTOK_TEMPLATE_SUBNAV,
  },
  {
    id: 'marketing',
    label: '营销活动',
    path: 'marketing',
    children: TIKTOK_MARKETING_SUBNAV,
  },
  {
    id: 'orders',
    label: '订单',
    path: 'orders',
    children: TIKTOK_ORDER_SUBNAV,
  },
  {
    id: 'analytics',
    label: '数据分析',
    path: 'analytics',
    children: TIKTOK_ANALYTICS_SUBNAV,
  },
  { id: 'finance', label: '财务账单', path: 'finance' },
  { id: 'shop', label: '店铺信息', path: 'shop' },
];

/**
 * @param {'full'|'semi'|'local'|string} [shopTypeSlug]
 */
export function navItemsForShopTypeSlug(shopTypeSlug) {
  const slug = normalizeTiktokShopTypeSlug(shopTypeSlug) || 'crossborder';
  const templateChildren = templateSubnavForShopTypeSlug(slug);

  return TIKTOK_NAV_BASE.map((item) => {
    if (item.id === 'templates') {
      return copyNavItem(item, { children: templateChildren });
    }
    if (slug !== 'full') return item;
    if (item.id === 'orders') {
      return copyNavItem(item, { children: TIKTOK_ORDER_SUBNAV });
    }
    return item;
  });
}

/** @deprecated 旧全托扁平菜单，保留空数组以免外部引用报错 */
export const TIKTOK_NAV_FULL_EXTRA = [];

/** 兼容旧引用：默认半托可见菜单 */
export const TIKTOK_NAV_ITEMS = TIKTOK_NAV_BASE;

/** 隐藏路由标题解析用（不进侧栏） */
export const TIKTOK_HIDDEN_NAV_ITEMS = [
  { id: 'stock', label: '仓库库存', path: 'stock' },
  { id: 'discuss', label: '商家改价（已下线）', path: 'discuss' },
  { id: 'price', label: '价格（已下线）', path: 'price' },
  {
    id: 'orders-hidden',
    label: '订单管理',
    path: 'orders',
    children: TIKTOK_ORDER_SUBNAV.filter((c) => c.id !== 'list'),
  },
  {
    id: 'fulfillment-legacy',
    label: '订单履约',
    path: 'fulfillment',
    children: TIKTOK_FULFILLMENT_ORDER_SUBNAV,
  },
  { id: 'finance', label: '财务账单', path: 'finance' },
  {
    id: 'templates-hidden',
    label: '模板库',
    path: 'templates',
    children: TIKTOK_TEMPLATE_SUBNAV_ALL.filter(
      (c) => !TIKTOK_TEMPLATE_SUBNAV.some((v) => v.id === c.id),
    ),
  },
];

bindNavLabels([
  TIKTOK_PRODUCT_SUBNAV,
  TIKTOK_TEMPLATE_SUBNAV_ALL,
  TIKTOK_COMPLIANCE_SUBNAV,
  TIKTOK_COMPLIANCE_SUBNAV_FULL_EXTRA,
  TIKTOK_ORDER_SUBNAV,
  TIKTOK_FULFILLMENT_ORDER_SUBNAV,
  TIKTOK_MARKETING_SUBNAV,
  TIKTOK_ANALYTICS_SUBNAV,
  TIKTOK_NAV_BASE,
  TIKTOK_HIDDEN_NAV_ITEMS,
]);

export { normalizeTiktokShopType };

/** @param {string} [shopType] @returns {'crossborder'|'local'|'full'|''} */
export function shopTypeSlugForLabel(shopType) {
  const normalized = normalizeTiktokShopType(shopType);
  if (normalized === '跨境') return 'crossborder';
  if (normalized === '本土') return 'local';
  if (normalized === '全托管') return 'full';
  return '';
}

/** @param {string} [slug] @returns {'跨境'|'本土'|'全托管'|''} */
export function shopTypeLabelForSlug(slug) {
  const s = String(slug || '').trim().toLowerCase();
  if (s === 'full' || s === 'full-service' || s === 'gs' || s === 'gsv2') return '全托管';
  if (s === 'crossborder' || s === 'cross-border' || s === 'cb' || s === 'semi') return '跨境';
  if (s === 'local' || s === 'l2l') return '本土';
  return '';
}

/** @deprecated 兼容旧名 */
export function trackForShopType(shopType) {
  return shopTypeSlugForLabel(shopType);
}

/** @deprecated 兼容旧名 */
export function shopTypeForTrack(track) {
  return shopTypeLabelForSlug(track);
}

export function normalizeTiktokShopTypeSlug(slug) {
  const label = shopTypeLabelForSlug(slug);
  return label ? shopTypeSlugForLabel(label) : '';
}

export function isTiktokShopTypeSlug(slug) {
  return Boolean(normalizeTiktokShopTypeSlug(slug));
}

export function isTiktokShopAuthorized(shop) {
  if (!shop || shop.authStatus !== 'connected') return false;
  if (String(shop?.platformCode || '').toUpperCase() !== 'TIKTOK') return false;
  if (shop.designSandbox === true) return true;
  const identifier = String(shop?.shopIdentifier || '');
  if (identifier.startsWith('TIKTOK-DESIGN-')) return true;
  if (shop.mockBinding === true) return false;
  if (shop.realAuthBound === false) return false;
  return true;
}

function resolveTiktokShopTypeSlug(shopTypeOrSlug) {
  if (isTiktokShopTypeSlug(shopTypeOrSlug)) {
    return normalizeTiktokShopTypeSlug(shopTypeOrSlug);
  }
  const fromLabel = shopTypeSlugForLabel(shopTypeOrSlug);
  if (fromLabel) return fromLabel;
  if (typeof window !== 'undefined') {
    const fromPath = parseTiktokShopTypeSlugFromPath(window.location.pathname);
    if (fromPath) return fromPath;
  }
  try {
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem(TIKTOK_WORKSPACE_SHOP_TYPE_KEY);
      const fromStored = shopTypeSlugForLabel(stored);
      if (fromStored) return fromStored;
    }
  } catch {
    /* ignore */
  }
  return 'crossborder';
}

/**
 * - buildTiktokPath('home') — 从 URL / storage 推断类型
 * - buildTiktokPath('full', 'home') — 显式类型
 */
export function buildTiktokPath(shopTypeOrSegment = '', maybeSegment) {
  let slug;
  let segment;
  if (maybeSegment !== undefined) {
    slug = resolveTiktokShopTypeSlug(shopTypeOrSegment);
    segment = maybeSegment;
  } else if (isTiktokShopTypeSlug(shopTypeOrSegment) && !String(shopTypeOrSegment).includes('/')) {
    slug = normalizeTiktokShopTypeSlug(shopTypeOrSegment);
    segment = '';
  } else {
    slug = resolveTiktokShopTypeSlug('');
    segment = shopTypeOrSegment;
  }
  const base = `${IPE_TIKTOK_BASE_PATH}/${slug || 'crossborder'}`;
  if (!segment) return base;
  return `${base}/${String(segment).replace(/^\//, '')}`;
}

export function buildTiktokChildPath(parentPath = '', childPath = '', shopTypeOrSlug = '') {
  const parent = String(parentPath || '').replace(/^\/+|\/+$/g, '');
  const slugArg = shopTypeOrSlug || undefined;
  if (!parent) {
    return slugArg !== undefined ? buildTiktokPath(slugArg, childPath) : buildTiktokPath(childPath);
  }
  if (!childPath) {
    return slugArg !== undefined ? buildTiktokPath(slugArg, parent) : buildTiktokPath(parent);
  }
  const seg = `${parent}/${String(childPath).replace(/^\//, '')}`;
  return slugArg !== undefined ? buildTiktokPath(slugArg, seg) : buildTiktokPath(seg);
}

export function buildTiktokProductChildPath(childPath = '', shopTypeOrSlug = '') {
  return buildTiktokChildPath('products', childPath, shopTypeOrSlug);
}

export function buildTiktokComplianceChildPath(childPath = '', shopTypeOrSlug = '') {
  return buildTiktokChildPath('compliance', childPath, shopTypeOrSlug);
}

export function buildTiktokTemplateChildPath(childPath = '', shopTypeOrSlug = '') {
  return buildTiktokChildPath('templates', childPath, shopTypeOrSlug);
}

export function buildTiktokGatePath({ shopId, shopType, redirect } = {}) {
  const params = new URLSearchParams();
  if (shopId != null && String(shopId).trim() !== '') {
    params.set('shopId', String(shopId).trim());
  }
  if (shopType) {
    const label = normalizeTiktokShopType(shopType) || shopTypeLabelForSlug(shopType) || String(shopType);
    if (label) params.set('shopType', label);
  }
  if (redirect) {
    params.set('redirect', String(redirect));
  }
  const qs = params.toString();
  return qs ? `${IPE_TIKTOK_GATE_PATH}?${qs}` : IPE_TIKTOK_GATE_PATH;
}

export function openTiktokGateForShop(shopId, options = {}) {
  ensureTiktokWorkspaceTokenShared();
  const path = buildTiktokGatePath({
    shopId,
    shopType: options.shopType,
    redirect: options.redirect,
  });
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  openUrlInNewTab(`${origin}${path}`);
}

export function ensureTiktokWorkspaceTokenShared() {
  try {
    if (typeof localStorage === 'undefined') return;
    if (localStorage.getItem(TIKTOK_WORKSPACE_TOKEN_KEY)) return;
    if (typeof sessionStorage === 'undefined') return;
    const fromSession = sessionStorage.getItem(TIKTOK_WORKSPACE_TOKEN_KEY);
    if (!fromSession) return;
    localStorage.setItem(TIKTOK_WORKSPACE_TOKEN_KEY, fromSession);
    sessionStorage.removeItem(TIKTOK_WORKSPACE_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export function openTiktokPathInNewTab(segmentOrPath = '') {
  ensureTiktokWorkspaceTokenShared();
  const path = String(segmentOrPath || '').startsWith('/')
    ? String(segmentOrPath)
    : buildTiktokPath(segmentOrPath);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  openUrlInNewTab(`${origin}${path}`);
}

export function isTiktokPlatformPath(pathname = '') {
  const p = pathname || '';
  return p === IPE_TIKTOK_BASE_PATH || p.startsWith(`${IPE_TIKTOK_BASE_PATH}/`);
}

/** 从 pathname 解析类型 slug */
export function parseTiktokShopTypeSlugFromPath(pathname = '') {
  const p = String(pathname || '');
  const prefix = `${IPE_TIKTOK_BASE_PATH}/`;
  if (!p.startsWith(prefix)) return '';
  const rest = p.slice(prefix.length);
  const first = rest.split('/')[0] || '';
  if (first === 'gate' || first === 'help') return '';
  return normalizeTiktokShopTypeSlug(first);
}

function navTitle(item) {
  return platformNavLabel(
    (key, opts) => i18n.t(key, { ns: 'workbench', defaultValue: '', ...opts }),
    item,
  );
}

/** 文档标题：根据 shopType + pathname 解析页面名 */
export function resolveTiktokPageMeta(shopTypeOrSlug, pathname = '') {
  const p = String(pathname || '');
  const slug = normalizeTiktokShopTypeSlug(shopTypeOrSlug)
    || parseTiktokShopTypeSlugFromPath(p)
    || 'crossborder';
  const prefix = `${IPE_TIKTOK_BASE_PATH}/${slug}/`;
  let rest = p.startsWith(prefix) ? p.slice(prefix.length) : '';
  rest = rest.replace(/\/+$/, '');
  if (!rest || rest === 'home') {
    return {
      title: i18n.t('platformNav.home', { ns: 'workbench', defaultValue: '首页' }),
      shopType: shopTypeLabelForSlug(slug),
    };
  }
  const top = rest.split('/')[0] || '';
  const navPool = [
    ...navItemsForShopTypeSlug(slug),
    ...TIKTOK_HIDDEN_NAV_ITEMS,
  ];
  for (const item of navPool) {
    if (item.path === top) {
      if (Array.isArray(item.children) && item.children.length) {
        const childSeg = rest.slice(top.length).replace(/^\//, '');
        const child = item.children.find((c) => {
          if (!c.path) return childSeg === '' || childSeg === top;
          return childSeg === c.path || childSeg.startsWith(`${c.path}/`);
        });
        if (child) {
          return {
            title: `${navTitle(item)} · ${navTitle(child)}`,
            shopType: shopTypeLabelForSlug(slug),
          };
        }
      }
      return { title: navTitle(item), shopType: shopTypeLabelForSlug(slug) };
    }
  }
  return {
    title: i18n.t('platformNav.workspace', { ns: 'workbench', defaultValue: '工作区' }),
    shopType: shopTypeLabelForSlug(slug),
  };
}

/** @deprecated */
export function normalizeTiktokTrack(track) {
  return normalizeTiktokShopTypeSlug(track) || 'crossborder';
}
